import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, KeyLabel } from 'src/Library/Types';
import {
  Form,
  Row,
  Col,
  Select,
  Input,
  Icon,
  Checkbox,
  message,
  TextArea,
  Spin,
} from 'src/Library/AntdLibComponents';
import { useGenericState } from 'src/Library/UseGenericState';
import { uploadSIModalStyles } from './UploadSIModal.styles';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { renderLabelAndInput, renderSelectDropDown } from 'src/Library/Common';
import { arrayToObject } from 'src/utils/utils';
import { Ports } from 'src/components/common/Types/CommonTypes';
import { getPorts } from 'src/api/containerTracking';
import { useDebounce } from 'src/hooks/useDebounce';
import { isValidArray, isValidString, stops } from 'src/Library/Helper';
import UploadSINotifyPartiesAddAddress from './UploadSINotifyPartiesAddAddress';
import classNames from 'classnames';
import { fetchShipmentInstructionsPartiesAddress } from 'src/api/shipmentExecution';
import { getSIPartyAddress } from 'src/components/pages/shipment-execution/SE.utils';
import { ShipmentData, SIPartyAddress, ShippingInstrutionsData } from 'src/components/pages/shipment-execution/SE.Types';
import { InquiryType } from 'src/Library/Constants';
import { getMandatoryFields } from './UploadSIModal.utils';
import Asterisk from 'src/components/common/Asterisk';
import PrimaryIcon from '../../../create-shipment/create-shipment.svg/primary';
import AddressIcon from '../../../create-shipment/create-shipment.svg/address';
import OrderDetailsIcon from '../../../create-shipment/create-shipment.svg/order-details';
import StakeholderIcon from '../../../create-shipment/create-shipment.svg/stakeholder';

interface IProps extends StylesProps<ReturnType<typeof uploadSIModalStyles>> {
  stakeHolders;
  shippingLines;
  form;
  allowedPickupAndDrop: string[];
  prefillingData;
  shipmentData: ShipmentData;
  setAddressList: (addressList) => void;
  addressList: SIPartyAddress;
  isLoading?: boolean;
  setCountryId: (countryId: string) => void;
  countryId: string;
  SICountryValidations;
  draftData: ShippingInstrutionsData;
  partyInvolvedWithAddress;
}

interface StateProps {
  partiesInvolved;
  searchTerm: string;
  ports: Ports [];
  isPortLoading: boolean;
  portType: string[];
  isMovementTypeChanged?: boolean;
  partiesInvolvedIndex?: number;
  partiesInvolvedFieldChanged?: boolean;
  isAddEditAddressModalVisible?: boolean;
  editAddressData?;
  isFetchingAddress?: boolean;
  selectedPartyType?: string;
}

const UploadSIModalStakeholderAndService = (props: IProps): React.ReactElement => {

  const {
    classes,
    form,
    stakeHolders,
    shippingLines,
    allowedPickupAndDrop,
    prefillingData,
    shipmentData,
    setAddressList,
    addressList,
    isLoading,
    setCountryId,
    countryId,
    SICountryValidations,
    draftData,
    partyInvolvedWithAddress,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    partiesInvolved: isValidArray(prefillingData?.partiesInvolved) ?
      prefillingData?.partiesInvolved :
      (isValidArray(partyInvolvedWithAddress) ? partyInvolvedWithAddress : [
        { type:{ key: 'Shipper', label: 'Shipper' },  name: shipmentData?.shipperName },
        { type: { key: 'Consignee', label: 'Consignee' }, name: shipmentData?.customerName },
        { type:{ key: 'FreightForwarder', label: 'Freight Forwarder' }, name: shipmentData?.ffName },
        { type:{ key: 'NotifyParty', label: 'Notify Party' } },
      ]) ,
    searchTerm: '',
    ports: [],
    isPortLoading: false,
    portType: [],
  });

  const {
    partiesInvolved,
    searchTerm,
    ports,
    isPortLoading,
    portType,
    isMovementTypeChanged,
    partiesInvolvedIndex,
    partiesInvolvedFieldChanged,
    isAddEditAddressModalVisible,
    editAddressData,
    isFetchingAddress,
    selectedPartyType,
  } = state;

  const {
    getFieldDecorator,
    setFieldsValue,
    getFieldValue,
    getFieldsValue,
  } = form;

  const mandatoryFields = getMandatoryFields({
    SICountryValidations,
    countryId,
    shipmentData
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  React.useEffect(() => {
    setState({ partiesInvolved: prefillingData?.partiesInvolved || partiesInvolved });
  }, [prefillingData?.partiesInvolved]);

  React.useEffect(
    () => {
      searchPorts(debouncedSearchTerm);
    },
    [debouncedSearchTerm, portType],
  );

  React.useEffect(() => {
    form.validateFields([
      'locationDetails.placeOfReceiptId',
      'locationDetails.placeOfDeliveryId',
      'locationDetails.blPrintDetails.placeOfReceipt',
      'locationDetails.blPrintDetails.placeOfDelivery',
    ], (err, values) => {
      if (err) return;
    });
  }, [isMovementTypeChanged])

  React.useEffect(() => {
    form.validateFields([
      `partiesInvolved[${partiesInvolvedIndex}].type`,
      `partiesInvolved[${partiesInvolvedIndex}].name`,
      `partiesInvolved[${partiesInvolvedIndex}].address`,
    ], (err, values) => {
      if (err) return;
    });
  }, [partiesInvolvedFieldChanged]);

  const searchPorts = async (queryString: string) : Promise<void> => {
    setState({ isPortLoading: true });
    let ports : Ports [] = [];
    const response = await getPorts({
      queryString,
      type: portType,
      searchByCountryName: true,
    });
    if (response.isSuccess) {
      ports = response.data;
    } else {
      message.error(response.errorMessage);
    }
    setState({ ports, isPortLoading: false });
  };

  const renderPartyName =  (index, initialValue?) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: 0 }}>
        {getFieldDecorator(`partiesInvolved[${index}].name`, {
          rules: [{
            validator: (rule, value, cb) => {
              if (!value && isPartyInvolvedMandatory(index, 'name')) {
                cb('Required');
              } else if (value?.length > 70) {
                cb('Maximum 70 characters allowed');
              } else cb();
            },
          }],
          initialValue,
        })(
          <Input
            className={classes.inputField}
            style={{ width: '95%' }}
            placeholder="Enter Name"
            disabled={isLoading}
            onChange={() => {
              setState({
                partiesInvolvedIndex: index,
                partiesInvolvedFieldChanged: !partiesInvolvedFieldChanged,
              });
            }}
          />
        )}
      </Form.Item>
    );
  };

  const renderAddressDropdown = (menu: React.ReactNode) => {
    if (isFetchingAddress) return <Spin style={{ marginLeft: '15px' }} />;
    return <React.Fragment>
      {menu}
      <div
        className={classes.createAddressStyle}
        onMouseDown={e => stops(e)}
        onClick={() => setState({ isAddEditAddressModalVisible: true })}>
        + Add Address
      </div>
    </React.Fragment>;
  };

  const getAddressList = async (index?, callApi?) : Promise<void> => {
    if (index) {
      const partyType = getFieldValue(`partiesInvolved[${index}].type`)?.key;
      if (!addressList || callApi) {
        setState({ isFetchingAddress: true });
        const response = await fetchShipmentInstructionsPartiesAddress({ shipmentId: shipmentData?.id });
        if (response.isSuccess) {
          setAddressList(response?.data || []);
          setState({ isFetchingAddress: false });
          if (!callApi && (!isValidArray(response?.data && response.data[partyType]))) {
            setState({ isAddEditAddressModalVisible: true });
          }
        } else {
          message.error(response.errorMessage);
          setState({ isFetchingAddress: false });
        }
      } else if (!isValidArray(addressList[partyType])) {
        setState({ isAddEditAddressModalVisible: true });
      }
    } else {
      setState({ isFetchingAddress: true });
      const response = await fetchShipmentInstructionsPartiesAddress({ shipmentId: shipmentData?.id });
      if (response.isSuccess) {
        setAddressList(response?.data || []);
        setState({
          isFetchingAddress: false,
        });
      } else {
        setState({ isFetchingAddress: false });
      }
    }
  };

  const renderPartyAddress = (index) : React.ReactElement => {
    const partyType = getFieldValue(`partiesInvolved[${index}].type`)?.key;
    return (
      <Form.Item style={{ marginBottom: 0 }}>
        {form.getFieldDecorator(`partiesInvolved[${index}].address`, {
          initialValue: partiesInvolved?.[index]?.address,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classNames(classes.selectField, classes.suffixIcon)}
            showSearch
            allowClear
            labelInValue
            disabled={isLoading}
            showArrow={isValidString(getFieldValue(`partiesInvolved[${index}].address`)?.key)}
            optionFilterProp="children"
            placeholder="Select Address"
            suffixIcon={
              <Icon
                type="edit"
                onClick={() => setState({
                  isAddEditAddressModalVisible: true,
                  editAddressData: getEditAddressData(index),
                })}
                theme="filled"
              />
            }
            onFocus={() => {
              if (!isValidString(getFieldValue(`partiesInvolved[${index}].type`)?.key)) {
                message.error('Please select Party Type');
                return;
              }
              getAddressList(index);
              setState({ selectedPartyType: getFieldValue(`partiesInvolved[${index}].type`)?.key });
            }}
            dropdownRender={menu => {
              if (addressList && isValidArray(addressList[partyType])) {
                return renderAddressDropdown(menu);
              } else {
                return <div></div>;
              }
            }}
            onChange={() => {
              setState({
                partiesInvolvedIndex: index,
                partiesInvolvedFieldChanged: !partiesInvolvedFieldChanged,
              });
            }}
          >
            {addressList && addressList[partyType]?.map(item => (
              <Select.Option key={item.id}>{getSIPartyAddress(item)}</Select.Option>
            ))}
          </Select>
        )}
      </Form.Item>
    );
  };

  const renderPartiesInvolved = () : React.ReactElement => {
    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>
          <div className={classes.boxIcon}>
            <StakeholderIcon  className={classes.boxIcon}/>
          </div>
          Parties <br/> Involved
        </Col>
        <Col span={20}>
          <Row style={{ fontSize: '12px', marginBottom: '8px', fontWeight: 600 }}>
            <Col span={4} className={classes.contentLabel}>
              Party
            </Col>
            <Col span={6} className={classes.contentLabel}>
              Name
            </Col>
            <Col span={10} className={classes.contentLabel}>
              Address
            </Col>
            <Col span={3} />
          </Row>
          {partiesInvolved?.map((item, index) => (
            <Row className={classes.partiesInvolvedRow} key={index}>
              <Col span={4}>
                {renderPartySelection(
                  index,
                  getInitialPartyValue(index, 'type'),
                )}
              </Col>
              <Col span={6}>
                {renderPartyName(
                  index,
                  getInitialPartyValue(index, 'name'),
                )}
              </Col>
              <Col span={10}>
                {renderPartyAddress(index)}
              </Col>
              {getFieldValue(`partiesInvolved[${index}].type`)?.key === 'Consignee' && <Col span={3}>
                {renderToOrder(index)}
              </Col>}
              {getFieldValue(`partiesInvolved[${index}].type`)?.key === 'NotifyParty' && <Col span={4}>
                {renderSameAsConsignee(index)}
              </Col>}
              <Col span={1}>
                {(partiesInvolved?.length > 4 && index >= 4) &&
                  <Icon type="close"
                    style={{ color: 'red', margin: '12px 0 0 6px' }}
                    onClick={() => handleRemove(index, 'partiesInvolved')}
                  />}
              </Col>
            </Row>
          ))}
          <div>
            <a
              className={classes.addMore}
              onClick={() => handleAddMore('partiesInvolved', partiesInvolved)}
            >
              + Add More
            </a>
          </div>
        </Col>
      </Row>
    );
  };

  const labelInputProps = {
    colSpan: {
      left: 6,
      right: 18,
    },
    form,
    classes,
    disabled: isLoading,
  };

  const renderCarrierSelect = () : React.ReactElement => {
    return (
      <React.Fragment>
        <Col span={5} className={classes.label}>
          Carrier<Asterisk />
        </Col>
        <Col span={18}>
        <Form.Item>
          {form.getFieldDecorator('bookingDetails.carrierId', {
            initialValue: prefillingData?.bookingDetails?.carrierId || undefined,
            rules: [{
              required: true, message: 'Required',
            }],
          })(
            <Select
              getPopupContainer={(trigger: any) => trigger.parentNode}
              className={classes.selectField}
              showSearch
              disabled={isLoading}
              allowClear
              optionFilterProp="children"
              placeholder="Select Carrier"
            >
              {shippingLines?.map(item => item.type === 'SEA' && (
                <Select.Option key={item.dbId}>{item.name}</Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </Col>
      </React.Fragment>
    );
  };

  const renderBookingDetails =  () : React.ReactElement => {
    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>
          <div className={classes.boxIcon}>
            <OrderDetailsIcon  className={classes.boxIcon}/>
          </div>
          Booking <br/> Details
        </Col>
        <Col span={20}>
          <Row>
            <Col span={8} style={{ display: 'flex' }}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: <>Booking No<Asterisk /></>,
                key: 'bookingDetails.bookingNumber',
                initialValue: prefillingData?.bookingDetails?.bookingNumber || undefined,
                placeholder: 'Enter Booking No',
                rules: [{
                  required: true, message: 'Required',
                }],
              })}
            </Col>
            <Col span={1} />
            <Col span={8}>
              {renderCarrierSelect()}
            </Col>
          </Row>
          <Row>
            <Col span={8} style={{ display: 'flex' }}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: <>Vessel<Asterisk /></>,
                key: 'bookingDetails.vessel',
                initialValue: prefillingData?.bookingDetails?.vessel,
                placeholder: 'Enter Vessel',
                rules: [
                  { required: true, message: 'Required' },
                  { max: 35, message: 'Maximum 35 characters allowed' },
                ],
              })}
            </Col>
            <Col span={1} />
            <Col span={8}>
            {renderLabelAndInput({
                ...labelInputProps,
                colSpan: {
                  left: 5,
                  right: 18,
                },
                label: <>Voyage<Asterisk /></>,
                key: 'bookingDetails.voyage',
                initialValue: prefillingData?.bookingDetails?.voyage,
                placeholder: 'Enter Voyage',
                rules: [
                  { required: true, message: 'Required' },
                  { max: 35, message: 'Maximum 35 characters allowed' }
                ],
              })}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderPort = (props, shouldSetCountryId?) : React.ReactElement => {
    const {
      key,
      label,
      portType,
      styles,
      rules,
      initialValue,
    } = props;
    return (
      <React.Fragment>
        <Col span={6} className={classes.label} style={{ ...styles }}>
          {label}
        </Col>
        <Col span={18}>
          <Form.Item style={{ marginBottom: '12px' }}>
            {getFieldDecorator(`locationDetails.${key}`, {
              rules,
              initialValue,
            })(
              <Select
                getPopupContainer={(trigger: any) => trigger.parentNode}
                onFocus={() => setState({ searchTerm: '', portType })}
                onSearch={(searchTerm) => setState({ searchTerm })}
                className={classes.selectField}
                showSearch
                disabled={isLoading}
                allowClear
                filterOption={(input, option) => true}
                labelInValue
                optionLabelProp="data-value"
                placeholder="Select Location"
                onChange={(val, options: any) => {
                  if (shouldSetCountryId) {
                    setCountryId(options?.props?.countryId);
                  }
                }}
                dropdownRender={menu => renderSelectDropDown(menu, isPortLoading)}
              >
                {ports?.map(port => (
                  <Select.Option key={port.dbId} data-value={`${port.name} (${port.code})`} { ...port }>
                    {`${port.name} (${port.code})`}
                    {port.type === 'DRY' &&
                      <span className={classes.dryPort}>ICD</span>
                    }
                  </Select.Option>)
                )}
              </Select>
            )}
          </Form.Item>
        </Col>
      </React.Fragment>
    );
  };

  const renderAddress = (key, label, initialValue?) : React.ReactElement => {
    return (
      <React.Fragment>
        <Col span={6} className={classes.label} style={{ marginTop: '6px' }}>
          {label}<Asterisk />
        </Col>
        <Col span={18}>
          <Form.Item style={{ marginBottom: 0 }}>
            {getFieldDecorator(`locationDetails.${key}`, {
              rules: [{ required: true, message: 'Required' }],
              initialValue,
            })(
              <TextArea
                placeholder="Enter Location"
                rows={2}
                disabled={isLoading}
                className={classes.textAreaField}
              />
            )}
          </Form.Item>
        </Col>
      </React.Fragment>
    );
  };

  const renderMovementTypeSelection =  () : React.ReactElement => {
    return (
      <React.Fragment>
        <Col span={6} className={classes.label} style={{ marginTop: '4px' }}>
          Movement<Asterisk />
        </Col>
        <Col span={18}>
          <Form.Item>
            {getFieldDecorator('locationDetails.movementType', {
              rules: [{
                required: true, message: 'Required',
              }],
              initialValue: prefillingData?.locationDetails?.movementType || undefined,
            })(
              <Select
                getPopupContainer={(trigger: any) => trigger.parentNode}
                allowClear
                disabled={isLoading}
                className={classes.selectField}
                placeholder={'Movement Type'}
                showSearch
                optionFilterProp="children"
                onChange={() => {
                  setState({ isMovementTypeChanged: !isMovementTypeChanged })
                }}
              >
                {allowedPickupAndDrop?.map(item => (
                  <Select.Option key={item}>
                    {item}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Col>
      </React.Fragment>
    );
  };

  const renderBLDetail = () : React.ReactElement => {
    return (
      <React.Fragment>
        <Row>
          <Col span={8} style={{ display: 'flex' }}>
            {renderLabelAndInput({
              ...labelInputProps,
              key: 'locationDetails.blPrintDetails.pol',
              label: 'POL',
              placeholder: 'Enter POL',
              initialValue: prefillingData?.locationDetails?.blPrintDetails?.pol,
              disabled: !getFieldValue('locationDetails.portOfLoadingId'),
            })}
          </Col>
          <Col span={1} />
          <Col span={8}>
            {renderLabelAndInput({
              ...labelInputProps,
              key: 'locationDetails.blPrintDetails.pod',
              placeholder: 'Enter POD',
              label: 'POD',
              initialValue: prefillingData?.locationDetails?.blPrintDetails?.pod,
              disabled: !getFieldValue('locationDetails.portOfDischargeId'),
            })}
          </Col>
        </Row>
        <Row>
          <Col span={8} style={{ display: 'flex' }}>
            {renderLabelAndInput({
              ...labelInputProps,
              key: 'locationDetails.blPrintDetails.placeOfReceipt',
              placeholder: 'Enter Place Of Receipt',
              label: 'Place of Receipt',
              labelStyles: { marginTop: '0px' },
              initialValue: prefillingData?.locationDetails?.blPrintDetails?.placeOfReceipt,
              disabled: !getFieldValue('locationDetails.placeOfReceiptId'),
            })}
          </Col>
          <Col span={1} />
          <Col span={8}>
            {renderLabelAndInput({
              ...labelInputProps,
              key: 'locationDetails.blPrintDetails.placeOfDelivery',
              placeholder: 'Enter Place Of Delivery',
              label: 'Place of Delivery',
              labelStyles: { marginTop: '0px' },
              initialValue: prefillingData?.locationDetails?.blPrintDetails?.placeOfDelivery,
              disabled: !getFieldValue('locationDetails.placeOfDeliveryId'),
            })}
          </Col>
        </Row>
      </React.Fragment>
    );
  }

  const renderLocationFields = () : React.ReactElement => {
    const movementType = getFieldValue('locationDetails.movementType');
    const isPOLDoorMovement = ['Door to Door', 'Door to Port'].includes(movementType);
    const isPODDoorMovement =  ['Door to Door', 'Port to Door'].includes(movementType);
    return (
      <React.Fragment>
        <Row>
          <Col span={8} style={{ display: 'flex' }}>
            {renderPort({
              key: 'portOfLoadingId',
              label: <>POL<Asterisk /></>,
              portType: ['WET'],
              rules: [{
                required: true, message: 'Required',
              }],
              initialValue: (!isEmpty(prefillingData?.locationDetails?.portOfLoadingId)) ? {
                key: prefillingData?.locationDetails?.portOfLoadingId?.key,
                label: prefillingData?.locationDetails?.portOfLoadingId?.label,
              } : undefined,
            },
            shipmentData?.type === InquiryType.EXPORT)}
          </Col>
          <Col span={1} />
          <Col span={8}>
            {renderPort({
              key: 'portOfDischargeId',
              label: <>POD<Asterisk /></>,
              portType: ['WET'],
              rules: [{
                required: true, message: 'Required',
              }],
              initialValue: (!isEmpty(prefillingData?.locationDetails?.portOfDischargeId)) ? {
                key: prefillingData?.locationDetails?.portOfDischargeId?.key,
                label: prefillingData?.locationDetails?.portOfDischargeId?.label,
              } : undefined,
            },
            shipmentData?.type === InquiryType.IMPORT)}
          </Col>
        </Row>
        <Row>
          <Col span={8} style={{ display: 'flex' }}>
            {renderPort({
              key: 'placeOfReceiptId',
              label: <>Place of Receipt{isPOLDoorMovement ? <Asterisk /> : null}</>,
              portType: ['WET', 'DRY'],
              style: { marginTop: '4px' },
              rules: [{
                required: isPOLDoorMovement, message: 'Required',
              }],
              initialValue: (!isEmpty(prefillingData?.locationDetails?.placeOfReceiptId)) ? {
                key: prefillingData?.locationDetails?.placeOfReceiptId?.key,
                label: prefillingData?.locationDetails?.placeOfReceiptId?.label,
              } : undefined,
            })}
          </Col>
          <Col span={1} />
          <Col span={8}>
            {renderPort({
              key: 'placeOfDeliveryId',
              label: <>Place of Delivery{isPODDoorMovement ? <Asterisk /> : null}</>,
              portType: ['WET', 'DRY'],
              style: { marginTop: '4px' },
              rules: [{
                required: isPODDoorMovement, message: 'Required',
              }],
              initialValue: (!isEmpty(prefillingData?.locationDetails?.placeOfDeliveryId)) ? {
                key: prefillingData?.locationDetails?.placeOfDeliveryId?.key,
                label: prefillingData?.locationDetails?.placeOfDeliveryId?.label,
              } : undefined,
            })}
          </Col>
        </Row>
        <Row>
          <Col span={8} style={{ display: 'flex' }}>
            {isPOLDoorMovement && renderAddress(
              'originAddress',
              'Origin Address',
              prefillingData?.locationDetails?.originAddress || undefined,
            )}
          </Col>
          <Col span={1} />
          <Col span={8}>
            {isPODDoorMovement && renderAddress(
              'destinationAddress',
              'Destination Address',
              prefillingData?.locationDetails?.destinationAddress || undefined,
            )}
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  const renderLocationDetails =  () : React.ReactElement => {
    return (
      <Row className={classes.box} style={{
        borderBottom: 'unset',
        marginBottom: 65,
      }}>
        <Col span={4} className={classes.contentHeading}>
          <div className={classes.boxIcon}>
            <AddressIcon className={classes.boxIcon} />
          </div>
          Address   
        </Col>
        <Col span={20}>
          <Row>
            <Col span={8} style={{ display: 'flex' }}>
              {renderMovementTypeSelection()}
            </Col>
          </Row>
          {renderLocationFields()}
          {renderBLDetailsCheckbox()}
          {getFieldValue('isDifferentBLDetails') && renderBLDetail()}
        </Col>
      </Row>
    );
  };

  const handleAddMore = (label, list) => {
    const newList = [
      ...list,
      {},
    ];
    setState({ [`${label}`]: newList });
  };

  const handleRemove = (index, label) => {
    const newList = form.getFieldValue(label);
    newList.splice(index, 1);
    setState({ [`${label}`]: newList });
    form.setFieldsValue({
      [`${label}`]: newList,
    });
  };

  const stakeholdersMap = arrayToObject(stakeHolders, 'key');

  const getRemainingStakeholders = () => {
    const stakeholdersAdded = getFieldsValue()?.partiesInvolved;
    stakeholdersAdded?.forEach(item => (
      delete stakeholdersMap[item.type?.key]
    ));
    return stakeholdersMap;
  };

  const isPartyInvolvedMandatory = (index, key) => {
    const arrayList = isValidArray(form.getFieldValue('partiesInvolved'))
    ? form.getFieldValue('partiesInvolved') : [{}];
    const {
      mandatoryPartyInvolvedFields,
    } = mandatoryFields;
    if (mandatoryPartyInvolvedFields[arrayList[index]?.type?.key]) return true;
    if (isValidArray(arrayList)) {
      return ((arrayList[index]?.type?.key === 'Shipper' ||
        arrayList[index]?.type?.key === 'Consignee') && key !== 'address' ) ||
        isValidString(arrayList[index]?.address?.key) ||
        (isValidString(arrayList[index]?.name) && !isValidString(arrayList[index]?.type));
    } 
    return;
  };

  const renderPartySelection =  (index, initialValue?) : React.ReactElement => {
    const remainingStakeholders = getRemainingStakeholders();
    return (
      <Form.Item style={{ marginBottom: 0 }}>
        {getFieldDecorator(`partiesInvolved[${index}].type`, {
          rules: [{
            validator: (rule, value, cb) => {
              if (!value && isPartyInvolvedMandatory(index, 'type')) {
                cb('Required');
              } else cb();
            },
          }],
          initialValue,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            allowClear
            labelInValue
            className={classes.selectField}
            placeholder={`Select Type`}
            dropdownMatchSelectWidth={false}
            showSearch
            style={{ width: '92%' }}
            optionFilterProp="children"
            disabled={index < 2 || isLoading}
            onChange={(option: KeyLabel) => {
              setState({
                partiesInvolvedIndex: index,
                partiesInvolvedFieldChanged: !partiesInvolvedFieldChanged,
              });
              if (option?.key === 'Shipper') {
                form.setFieldsValue({ [`partiesInvolved[${index}].name`]: shipmentData?.shipperName })
              } else {
                form.setFieldsValue({
                  [`partiesInvolved[${index}].name`]: undefined,
                  [`partiesInvolved[${index}].address`]: undefined,
                })
              }
            }}
          >
            {remainingStakeholders && Object.keys(remainingStakeholders)?.map(key => (
              <Select.Option key={remainingStakeholders[key].key}>
                {remainingStakeholders[key].label}
              </Select.Option>
            ))}
          </Select>
        )}
      </Form.Item>
    );
  };

  const getInitialPartyValue = (index, key) => {
    if ((!isEmpty(partiesInvolved) && partiesInvolved[index])) {
      return partiesInvolved[index][key];
    }
    return undefined;
  };

  const renderBLDetailsCheckbox = () : React.ReactElement => {
    return (
      <Form.Item>
        {getFieldDecorator('isDifferentBLDetails', {
          initialValue: prefillingData?.isDifferentBLDetails,
          valuePropName: 'checked',
        })(
          <Checkbox
            disabled={isLoading}
            style={{ marginTop: '18px' }}
          >
            I want to print different details on B/L
          </Checkbox>
        )}
      </Form.Item>
    );
  }

  const renderToOrder = (index) : React.ReactElement => {
    return (
      <Form.Item style={{margin: '-10px 8px'}}>
        {getFieldDecorator(`partiesInvolved[${index}].toOrder`, {
          initialValue: partiesInvolved[index]?.toOrder || false,
          valuePropName: 'checked',
        })(
          <Checkbox
            onChange={e => {
              if (e.target.checked) {
                setFieldsValue({ [`partiesInvolved[${index}].name`]: 'To Order' });
              } else {
                setFieldsValue({ [`partiesInvolved[${index}].name`]: undefined });
              }
            }}
            disabled={isLoading}
            style={{ marginTop: '8px', fontSize: '12px' }}
          >
            To Order
          </Checkbox>
        )}
      </Form.Item>
    );
  };

  const renderSameAsConsignee = (index) : React.ReactElement => {
    return (
      <Form.Item style={{margin: '-10px 8px'}}>
        {getFieldDecorator(`partiesInvolved[${index}].sameAsConsignee`, {
          initialValue: partiesInvolved[index]?.sameAsConsignee || false,
          valuePropName: 'checked',
        })(
          <Checkbox
            onChange={e => {
              if (e.target.checked) {
                const consigneeValue = getFieldsValue().partiesInvolved[1];
                setFieldsValue({ [`partiesInvolved[${index}].name`]:  consigneeValue.name });
              } else {
                setFieldsValue({ [`partiesInvolved[${index}].name`]: undefined });
              }
            }}
            disabled={isLoading}
            style={{ marginTop: '8px', fontSize: '12px' }}
          >
            Same As Consignee
          </Checkbox>
        )}
      </Form.Item>
    );
  };

  const getEditAddressData = (index) => {
    const party = getFieldValue(`partiesInvolved[${index}]`);
    const addressData = addressList &&
      addressList[party?.type?.key]?.find(item => item.id === party?.address?.key);
    return addressData;
  };

  const renderAddEditPartyAddress = () : React.ReactElement =>{
    return (
      <UploadSINotifyPartiesAddAddress
        isVisible={isAddEditAddressModalVisible}
        onCancel={() => {
          setState({
            isAddEditAddressModalVisible: false,
            editAddressData: undefined,
          })
        }}
        onSubmit={() => {
          getAddressList(0, true);
          setState({
            isAddEditAddressModalVisible: false,
            editAddressData: undefined,
          })
        }}
        editAddressData={editAddressData}
        shipmentId={shipmentData?.id}
        partyType={selectedPartyType}
      />
    );
  };

  return (
    <React.Fragment>
      {renderPartiesInvolved()}
      {renderBookingDetails()}
      {renderLocationDetails()}
      {isAddEditAddressModalVisible && renderAddEditPartyAddress()}
    </React.Fragment>
  );
};

const mapStateToProps = ({ masterData }, ownProps) => ({
  stakeHolders: masterData.shippingInstructionsMasterData?.stakeHolders,
  allowedPickupAndDrop: masterData.execution?.allowedPickupAndDrop,
  shippingLines: masterData?.globalShippingLines,
  SICountryValidations: masterData.shippingInstructionsMasterData?.SICountryValidations,
});

export default connect(
  mapStateToProps,
  null,
)(React.memo(withStyles(uploadSIModalStyles)(UploadSIModalStakeholderAndService)));